import { Form, Formik, FormikProps } from 'formik';
import { FunctionComponent, useCallback, useRef, useState } from 'react';
import CheckBox from '../../forms/CheckBox';
import DateField from '../../forms/DateField';
import MaxTextField from '../../forms/MaxTextField';
import RadioButton from '../../forms/RadioButton';
import RadioButtonGroup from '../../forms/RadioButtonGroup';
import TextArea from '../../forms/TextArea';
import TimeField from '../../forms/TimeField';
import OverlayAlertModal from '../../modal/OverlayAlertModal';
import OverlayConfirmModal from '../../modal/OverlayConfirmModal';
import { usePropertyAssociationContext } from '../../propertyAssociation/PropertyAssociationContext';
import { PinboardMessageFormModel } from '../models/PinboardMessageFormModel';
import PinboardMessageFileInput from './PinboardMessageFileInput';
import styles from './PinboardMessageForm.module.scss';
import { pinboardMessageValidationSchema } from './PinboardMessageForm.validation';
import PublishToSeveralOption from './PublishToSeveralOption';

type Props = {
    isNew?: boolean;
    messageForm: PinboardMessageFormModel;
    submitBtnText?: string;
    user?: { imagePath: string; firstName: string; lastName: string };
    onValidSubmit: (values: PinboardMessageFormModel) => void;
    onCancel: () => void;
};

const PinboardMessageForm: FunctionComponent<Props> = ({ isNew = false, messageForm, onCancel, onValidSubmit, submitBtnText }) => {
    const formikRef = useRef<FormikProps<any>>(null);
    const { currentArea, currentPropertyAssociation, allPropertyAssociations, isRBHyr } = usePropertyAssociationContext();
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const canPublishToSeveral = isRBHyr && allPropertyAssociations.length > 1;

    const handleSubmit = useCallback(
        (values: PinboardMessageFormModel) => {
            const target = values.target;
            if (isNew && (target === 'all' || target === 'some')) {
                setShowConfirmModal(true);
            } else {
                onValidSubmit(values);
            }
        },
        [onValidSubmit],
    );

    const onTrySubmit = useCallback(() => {
        if (!pinboardMessageValidationSchema.isValidSync(formikRef.current?.values)) {
            setShowErrorModal(true);
        }
    }, []);

    const onAddFiles = useCallback((newFiles: File | File[]) => {
        if (!formikRef.current) {
            return;
        }

        const { files, filePaths } = formikRef.current.values;

        if (!Array.isArray(newFiles)) {
            newFiles = [newFiles];
        }

        // Remove all files that are already in the files or oldFiles array
        newFiles = newFiles.filter((file: File) => !files.some((f: File) => f.name === file.name) && !(filePaths && filePaths.some((f: string) => f.endsWith('/' + file.name))));

        formikRef.current.setFieldValue('files', [...files, ...newFiles]);
    }, []);

    const onDeleteOldFile = useCallback((fileName: string) => {
        if (!formikRef.current) {
            return;
        }

        const { filePaths } = formikRef.current.values;
        formikRef.current.setFieldValue(
            'filePaths',
            filePaths.filter((path: string) => path !== fileName),
        );
    }, []);

    const onDeleteNewFile = useCallback((index: number) => {
        if (!formikRef.current) {
            return;
        }

        const { files } = formikRef.current.values;
        formikRef.current.setFieldValue(
            'files',
            files.filter((file: File, i: number) => i !== index),
        );
    }, []);

    return (
        <>
            <Formik initialValues={messageForm} innerRef={formikRef as any} enableReinitialize={true} validationSchema={pinboardMessageValidationSchema} validateOnMount={true} onSubmit={handleSubmit}>
                {({ values, errors, touched }) => {
                    const formProps = { values, errors, touched };

                    return (
                        <Form>
                            <div className="col-12 col-md-8 px-4 ms-2">
                                <div className="mb-5 position-relative">
                                    <MaxTextField maxLength={30} label="Rubrik" name="title" {...formProps} />
                                </div>
                                <TextArea label="Meddelande" name="text" {...formProps}></TextArea>
                                <CheckBox name="isPinned" label="Fäst inlägget överst i listan av meddelanden" {...formProps} />
                                <fieldset className={styles.datetimeFieldset}>
                                    <legend className="visually-hidden">Publiceringsdatum</legend>
                                    <div className="row g-3">
                                        <div className="col-12 col-lg-7 mt-lg-0">
                                            <DateField name="date" label="Publiceringsdatum" minDate={new Date()} hideError {...formProps} />
                                        </div>
                                        <div className="col-12 col-lg-5 mt-lg-0">
                                            <TimeField name="time" label="Tid" hideError {...formProps} />
                                        </div>
                                    </div>
                                    {(errors.date || errors.time) && (touched.date || touched.time) && <div className="field-error text-danger">{(errors.date || errors.time) as string}</div>}
                                </fieldset>
                                <fieldset className={styles.datetimeFieldset}>
                                    <legend className="visually-hidden">Avpubliceringsdatum</legend>
                                    <div className="row g-3">
                                        <div className="col-12 col-lg-7 mt-lg-0">
                                            <DateField name="endDate" label="Avpubliceringsdatum" hideError {...formProps} />
                                        </div>
                                        <div className="col-12 col-lg-5 mt-lg-0">
                                            <TimeField name="endTime" label="Tid" hideError {...formProps} />
                                        </div>
                                    </div>
                                    {(errors.endDate || errors.endTime) && (touched.endDate || touched.endTime) && (
                                        <div className="field-error text-danger">{(errors.endDate || errors.endTime) as string}</div>
                                    )}
                                </fieldset>
                                {isNew && currentArea?.propertyAssociationAreaId && (
                                    <div className="my-4">
                                        <p className="my-0">Välj mottagare</p>
                                        <RadioButtonGroup {...formProps} name="target">
                                            {canPublishToSeveral && <PublishToSeveralOption propertyAssociations={allPropertyAssociations} />}
                                            <div className="my-3">
                                                <RadioButton
                                                    name="target"
                                                    label={`Alla boende${canPublishToSeveral ? ' i ' + currentPropertyAssociation!.name : ''} (i alla områden)`}
                                                    value="current"
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <RadioButton name="target" label={`Endast boende i ${currentArea.name}`} value={currentArea.propertyAssociationAreaId} />
                                            </div>
                                        </RadioButtonGroup>
                                    </div>
                                )}
                                <div className="mb-2">
                                    <p className="my-0">Ska meddelandet gå att svara på?</p>
                                    <p className="meta mb-4 mt-2">Ska de boende kunna svara på meddelandet via appen.</p>
                                    <RadioButtonGroup {...formProps} name="canUserAnswer">
                                        <div className="my-3">
                                            <RadioButton name="canUserAnswer" label="Nej" value="nej" />
                                        </div>
                                        <div className="mt-3">
                                            <RadioButton name="canUserAnswer" label="Ja" value="ja" />
                                        </div>
                                    </RadioButtonGroup>
                                </div>
                                <PinboardMessageFileInput
                                    oldFiles={values.filePaths}
                                    newFiles={values.files}
                                    onAddFiles={onAddFiles}
                                    onDeleteOldFile={onDeleteOldFile}
                                    onDeleteNewFile={onDeleteNewFile}
                                />
                            </div>
                            <div className="paper__buttons">
                                <div className="paper__secondary-buttons">
                                    <button type="button" className="btn btn-lg btn-secondary" onClick={onCancel}>
                                        Avbryt
                                    </button>
                                </div>
                                <div className="paper__primary-buttons">
                                    <button type="submit" className="btn btn-lg btn-success" onClick={onTrySubmit}>
                                        {submitBtnText || 'Spara'}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            <OverlayAlertModal
                heading="Det saknas information"
                text={`Det verkar som att viss obligatorisk information för meddelandet saknas. När du fyllt i allt kommer du kunna ${submitBtnText?.toLocaleLowerCase() || 'spara'} det.`}
                visible={showErrorModal}
                onClick={() => setShowErrorModal(false)}
            />
            <OverlayConfirmModal
                heading="Publicera inlägg till engagemang"
                text="Är du säker på att du vill publicera inlägget till alla boende i följande engagemang?"
                visible={showConfirmModal}
                okButtonText="Publicera"
                cancelButtonText="Avbryt"
                onCancel={() => setShowConfirmModal(false)}
                onSubmit={() => {
                    setShowConfirmModal(false);
                    if (formikRef.current) {
                        onValidSubmit(formikRef.current.values);
                    }
                }}
            >
                {allPropertyAssociations.length > 0 && formikRef.current && (
                    <ul className="mb-5 mx-auto text-start" style={{ width: 'fit-content' }}>
                        {allPropertyAssociations
                            .filter(
                                (pa) =>
                                    formikRef.current!.values.target === 'all' ||
                                    (formikRef.current!.values.target === 'some' && formikRef.current!.values.targetAssociations?.includes(pa.propertyAssociationId)) ||
                                    (formikRef.current!.values.target === 'current' && pa.propertyAssociationId === currentPropertyAssociation?.propertyAssociationId),
                            )
                            .map((association) => (
                                <li key={association.propertyAssociationId}>
                                    <span>{association.name}</span>
                                </li>
                            ))}
                    </ul>
                )}
            </OverlayConfirmModal>
        </>
    );
};

export default PinboardMessageForm;
